import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FiX, FiEdit3, FiCheckCircle, FiAlertCircle, FiInfo, FiAlertTriangle, FiMoon, FiSun } from "react-icons/fi";
import MessageContent from "../MessageContent";
import MessageInput from "../MessageInput";
import FollowupSuggestions from "../FollowupSuggestions";
import ActionControllerComponent from "../ActionControllerComponent";
import CitationsComponent from "../CitationsComponent";
import Tooltip from "../Tooltip";
import { AIAssistantAblyProvider } from "../../contexts/AIAssistantAblyContext";
import AssistantSocketListener from "../AssistantSocketListener";
import {
    selectIsChatOpen,
    selectIsChatExpanded,
} from "../../Selectors/AssistantSelector";
import {
    toggleChatVisibility,
    setChatExpanded,
    assistantResponding,
    deleteMessagesKeepingInitial,
    hideCitations,
    clearCitations,
    clearHistoryMetadata,
    clearFollowupSuggestions,
    setTaskStatusMessage,
    setWelcomeSuggestions,
    clearWelcomeSuggestions,
} from "../../state/actions";
import { initiateSession } from "../../utils/SessionManager";
import { useScreenContext } from "../../hooks/useScreenContext";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import "./ChatWindow.scss";

const ToastStack = ({ toasts, onClose }) => {
    const typeStyles = {
        success: { icon: <FiCheckCircle />, className: "success", defaultDescription: "The action completed successfully." },
        error: { icon: <FiAlertCircle />, className: "error", defaultDescription: "Something went wrong." },
        warning: { icon: <FiAlertTriangle />, className: "warning", defaultDescription: "Please review this warning." },
        info: { icon: <FiInfo />, className: "info", defaultDescription: "FYI." },
    };

    return (
        <div className="chat-toast-stack">
            {toasts.map((toast) => {
                const style = typeStyles[toast.type] || typeStyles.info;
                return (
                    <div
                        key={toast.id}
                        className={`chat-toast-card ${style.className}`}
                    >
                        <div className="chat-toast-icon">{style.icon}</div>
                        <div className="chat-toast-content">
                            <div className="chat-toast-title">{toast.title}</div>
                            <div className="chat-toast-description">
                                {toast.description || style.defaultDescription}
                            </div>
                        </div>
                        <button
                            className="chat-toast-close"
                            type="button"
                            aria-label="Close toast"
                            onClick={() => onClose(toast.id)}
                        >
                            <FiX />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

const ChatWindowContent = () => {
    const dispatch = useDispatch();
    const isChatOpen = useSelector(selectIsChatOpen);
    const isExpanded = useSelector(selectIsChatExpanded);
    const messages = useSelector((state) => state.chat.messages);
    const userDetail = useSelector((state) => state.userDetail);
    const {
        citations,
        isActionControllerVisible,
        isCitationsExpanded,
        actionControllerContent,
        followupSuggestions = [],
    } = useSelector((state) => state.chat);
    const hasMessages = messages.length > 0;
    const [clearState, setClearState] = useState("idle");
    const [toasts, setToasts] = useState([]);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isScrolledState, setIsScrolledState] = useState(false);
    const { buildCurrentScreenContext } = useScreenContext();

    React.useEffect(() => {
        const handleScrollState = (e) => {
            setIsScrolledState(e.detail.isScrolled);
        };
        window.addEventListener("chat-scroll-state", handleScrollState);
        return () => window.removeEventListener("chat-scroll-state", handleScrollState);
    }, []);

    const addToast = ({ type = "info", title, description, duration = 2500 }) => {
        const id = Math.random().toString(36).slice(2);
        const toast = { id, type, title, description };
        setToasts((prev) => [...prev, toast]);
        if (duration !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        }
        return id;
    };

    const updateToast = (id, updates) => {
        setToasts((prev) =>
            prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
        );
        if (updates.duration !== undefined && updates.duration !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, updates.duration);
        }
    };
    const handleClearChat = async () => {
        setClearState("clearing");
        try {
            const currentScreenContext = buildCurrentScreenContext();
            const res = await initiateSession(userDetail?.id, currentScreenContext);
            if (res?.success) {
                dispatch(deleteMessagesKeepingInitial(0));
                dispatch(clearCitations());
                dispatch(clearHistoryMetadata());
                dispatch(clearFollowupSuggestions());
                if (res.suggestions) {
                    dispatch(setWelcomeSuggestions(res.suggestions));
                } else {
                    dispatch(clearWelcomeSuggestions());
                }
                dispatch(assistantResponding(false));
                dispatch(setTaskStatusMessage({ type: "task_status_end" }));
                setClearState("idle");
                addToast({
                    type: "success",
                    title: "New chat ready.",
                    description: "You can start typing your next question.",
                    duration: 2000,
                });
            } else {
                throw new Error("Clear chat failed");
            }
        } catch (error) {
            setClearState("failed");
            addToast({
                type: "error",
                title: "Failed to start a new chat.",
                description: "Please try again.",
                duration: 2500,
            });
            setTimeout(() => {
                setClearState("idle");
            }, 2000);
        }
    };

    const toggleCitations = () => {
        if (isCitationsExpanded) {
            dispatch(hideCitations());
        }
    }

    const handleSuggestionClick = (text) => {
        if (!text) return;
        window.dispatchEvent(
            new CustomEvent("ai-assistant-send-suggestion", { detail: { text } })
        );
    };

    const handleExpand = () => {
        dispatch(setChatExpanded(!isExpanded));
    };

    const handleClose = () => {
        dispatch(toggleChatVisibility(false));
        dispatch(setChatExpanded(false));
    };

    const toggleTheme = () => {
        setIsDarkMode((prev) => !prev);
    };

    return (
        <div
            className={` card-container chat-container
    ${isExpanded ? "expanded" : ""}
    ${!isChatOpen ? "collapsed" : ""}
    ${isActionControllerVisible ? "action-controller-visible" : ""}
    ${isDarkMode ? "ai-dark" : ""}`}
        >


            <div className="card-header">
                <div className="card-title">
                    <span>{AI_ASSISTANT_CONSTANTS.CHAT.TITLE}</span>
                    <span className="beta-tag" aria-label="Assistant version">
                        {AI_ASSISTANT_CONSTANTS.CHAT.VERSION}
                    </span>
                </div>

                <div className="card-header-actions">
                    <button
                        type="button"
                        className={`icon-button ${clearState !== "idle" ? "disabled" : ""}`}
                        onClick={clearState === "idle" ? handleClearChat : undefined}
                        disabled={clearState !== "idle"}
                        aria-label="Start new chat"
                    >
                        <Tooltip content="New chat" placement="bottom">
                            <span>
                                <FiEdit3 size={16} />
                            </span>
                        </Tooltip>
                    </button>

                    <button type="button" className="icon-button" onClick={toggleTheme} aria-label="Toggle theme">
                        <Tooltip content={isDarkMode ? "Switch to light" : "Switch to dark"} placement="bottom">
                            <span>
                                {isDarkMode ? <FiSun size={16} /> : <FiMoon size={16} />}
                            </span>
                        </Tooltip>
                    </button>

                    <button type="button" className="icon-button" onClick={handleClose} aria-label="Close chat">
                        <Tooltip content="Close" placement="bottom">
                            <span>
                                <FiX size={16} />
                            </span>
                        </Tooltip>
                    </button>
                </div>
            </div>

            <div className={`chat-body ${hasMessages ? "has-messages" : "no-messages"}`}>
                <MessageContent />
                <div className={`chat-footer-overlay ${isScrolledState ? "is-scrolled" : ""}`}>
                    {followupSuggestions.length > 0 && (
                        <div className="followup-suggestions-footer">
                            <FollowupSuggestions
                                suggestions={followupSuggestions}
                                onSuggestionClick={handleSuggestionClick}
                            />
                        </div>
                    )}
                    <MessageInput />
                </div>
            </div>
            {isActionControllerVisible && (
                <ActionControllerComponent
                    title="Information Sources"
                    isVisible={isCitationsExpanded}
                    isExpanded={isCitationsExpanded}
                    toggleExpand={toggleCitations}
                >
                    {actionControllerContent === 'Citations' && <CitationsComponent citations={citations} />}
                </ActionControllerComponent>
            )}
            <ToastStack
                toasts={toasts}
                onClose={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
            />
        </div>
    );
};

const ChatWindow = () => (
    <AIAssistantAblyProvider>
        <AssistantSocketListener />
        <ChatWindowContent />
    </AIAssistantAblyProvider>
);

export default ChatWindow;
